/**
 * Pure logic for the Alice composer's `/send` command mode.
 *
 * Kept free of React so the parsing and idempotency rules — the parts that
 * carry real risk (double sends, wrong path) — are unit-tested directly.
 */

export const SEND_COMMAND = '/send';

export interface ParsedSendCommand {
  /** Input begins with the `/send` command token. */
  isCommand: boolean;
  /** Command with no message body (bare `/send`) → open the composer. */
  isBare: boolean;
  /** The message body after `/send` (trimmed of the token, not the content). */
  body: string;
}

const SEND_RE = /^\/send(\s|$)/i;

/**
 * Recognize `/send` at the very start of the input. `/sender` is not a command;
 * leading whitespace before the token is not a command either.
 */
export function parseSendCommand(input: string): ParsedSendCommand {
  if (!SEND_RE.test(input)) return { isCommand: false, isBare: false, body: '' };
  const body = input.replace(/^\/send\s*/i, '');
  return { isCommand: true, isBare: body.trim().length === 0, body };
}

/** True when the input is a partial command prefix worth suggesting `/send` for. */
export function isSendSuggestionPrefix(input: string): boolean {
  return /^\/s?e?n?d?$/i.test(input) && input.length >= 1 && !parseSendCommand(input).isCommand;
}

/** Normalized dedupe key so an identical resend is recognized. */
export function buildSendKey(clientId: string, body: string): string {
  return `${clientId}::${body.trim().toLowerCase()}`;
}

export interface SendGuard {
  /** Reserve a send for `key`; false if one is in flight or completed recently. */
  tryBegin(key: string, now?: number): boolean;
  /** Mark a successful send: clears the reservation and records completion time. */
  end(key: string, now?: number): void;
  /** Mark a failed send: clears the reservation without blocking an immediate retry. */
  release(key: string): void;
}

/**
 * Guards against duplicate thread/notification creation when a submit is
 * retried (double Enter/click, fast resend). An identical `key` cannot start
 * again while one is in flight, nor within `windowMs` of the last completion.
 */
export function createSendGuard(windowMs = 4000): SendGuard {
  const inFlight = new Set<string>();
  const recent = new Map<string, number>();
  return {
    tryBegin(key, now = Date.now()) {
      if (inFlight.has(key)) return false;
      const last = recent.get(key);
      if (last !== undefined && now - last < windowMs) return false;
      inFlight.add(key);
      return true;
    },
    end(key, now = Date.now()) {
      inFlight.delete(key);
      recent.set(key, now);
    },
    release(key) {
      inFlight.delete(key);
    },
  };
}
